import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  FiArrowLeft, 
  FiPlusCircle, 
  FiKey, 
  FiCopy,
  FiList,
  FiCheckSquare
} from 'react-icons/fi';
import axios from 'axios';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';

// 获取应用列表函数
const fetchApplications = async () => {
  const response = await axios.get('/api/applications');
  return response.data.applications;
};

// 生成授权码函数
const generateLicenses = async (data) => {
  const response = await axios.post('/api/licenses/generate', data);
  return response.data;
};

const CreateLicense = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // 获取URL参数中的应用ID
  const searchParams = new URLSearchParams(location.search);
  const applicationIdParam = searchParams.get('applicationId');
  
  // 状态
  const [count, setCount] = useState(1);
  const [selectedAppId, setSelectedAppId] = useState(applicationIdParam || '');
  const [customerName, setCustomerName] = useState('');
  const [remark, setRemark] = useState('');
  const [generatedLicenses, setGeneratedLicenses] = useState([]);
  const [selectedLicenses, setSelectedLicenses] = useState([]);
  // 生成时直接激活 + 到期时间(默认激活, 到期默认永久)
  const [activateNow, setActivateNow] = useState(true);
  const [expireDate, setExpireDate] = useState(''); // yyyy-mm-dd, 空=永久
  
  // 获取应用列表
  const { data: applications, isLoading: appsLoading } = useQuery(
    'applications',
    fetchApplications
  );
  
  // 生成授权码突变
  const generateMutation = useMutation(generateLicenses, {
    onSuccess: (data) => {
      setGeneratedLicenses(data.licenses);
      setSelectedLicenses(data.licenses.map(license => license.licenseKey));
      toast.success(`成功生成${data.licenses.length}个授权码`);
    },
    onError: (error) => {
      console.error('生成授权码错误:', error);
      toast.error(error.response?.data?.message || '生成授权码失败');
    }
  });
  
  // 处理生成
  const handleGenerate = () => {
    if (!selectedAppId) {
      toast.error('请选择应用');
      return;
    }
    
    if (count < 1 || count > 100) {
      toast.error('生成数量必须在1-100之间');
      return;
    }

    generateMutation.mutate({
      count,
      applicationId: parseInt(selectedAppId, 10),
      customerName: customerName.trim() || undefined,
      remark: remark.trim() || undefined,
      activate: activateNow,
      // 传当天 23:59:59 的 ISO 串, 避免时区把到期日漂移
      expiresAt: expireDate ? new Date(`${expireDate}T23:59:59`).toISOString() : undefined
    });
  };
  
  const copytxt = (text) => {
    // 创建临时文本区域
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    
    // 选择文本区域中的文本
    textarea.select();
    textarea.setSelectionRange(0, 99999); // 对于手机，选择范围
    
    // 执行复制命令
    document.execCommand('copy');
    
    // 移除临时文本区域
    document.body.removeChild(textarea);
};
  
  // 复制授权码
  const copyToClipboard = (text) => {
    copytxt(text);
    toast.success('授权码已复制到剪贴板');
  };
  
  // 复制选中的授权码
  const copySelectedLicenses = () => {
    const text = selectedLicenses.join('\n');
    copytxt(text);
    toast.success(`已复制${selectedLicenses.length}个授权码到剪贴板`);
  };
  
  // 选择或取消选择授权码
  const toggleSelectLicense = (licenseKey) => {
    if (selectedLicenses.includes(licenseKey)) {
      setSelectedLicenses(selectedLicenses.filter(key => key !== licenseKey));
    } else {
      setSelectedLicenses([...selectedLicenses, licenseKey]);
    }
  };
  
  // 全选或取消全选
  const toggleSelectAll = () => {
    if (selectedLicenses.length === generatedLicenses.length) {
      setSelectedLicenses([]);
    } else {
      setSelectedLicenses(generatedLicenses.map(license => license.licenseKey));
    }
  };
  
  // 页面动画
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3
      }
    }
  };
  
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="h-full"
    >
      {/* 页面标题 */}
      <motion.div variants={itemVariants} className="flex items-center mb-6">
        <Button
          onClick={() => navigate('/licenses')}
          variant="ghost"
          icon={<FiArrowLeft />}
          className="p-2 mr-4"
        />
        <div>
          <h1 className="text-2xl font-bold text-[#1f2328]">生成授权码</h1>
          <p className="text-[#656d76] mt-1">批量生成新的授权码</p>
        </div>
      </motion.div>
      
      {/* 生成表单 */}
      <motion.div
        variants={itemVariants}
        className="bg-white rounded-md shadow-sm p-6 mb-6"
      >
        <div className="mb-6 space-y-5">
          {/* 应用选择 */}
          <div>
            <label className="block text-[#57606a] text-sm font-medium mb-2">
              选择应用 *
            </label>
            <select
              value={selectedAppId}
              onChange={(e) => setSelectedAppId(e.target.value)}
              className="w-full bg-[#f6f8fa] rounded-md py-3 px-4 text-[#1f2328] border border-[#d0d7de] focus:outline-none brand-ring"
              required
              disabled={appsLoading}
            >
              <option value="">-- 选择应用 --</option>
              {applications?.map(app => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* 生成数量 */}
          <div>
            <label className="block text-[#57606a] text-sm font-medium mb-2">
              生成数量 (1-100)
            </label>
            <div className="flex flex-wrap gap-4">
              <input
                type="number"
                min="1"
                max="100"
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value) || 1)}
                className="flex-1 min-w-[120px] bg-[#f6f8fa] rounded-md py-3 px-4 text-[#1f2328] border border-[#d0d7de] focus:outline-none brand-ring"
              />
              <Button
                onClick={handleGenerate}
                variant="primary"
                icon={<FiPlusCircle />}
                disabled={generateMutation.isLoading || !selectedAppId}
              >
                {generateMutation.isLoading ? '生成中...' : '生成授权码'}
              </Button>
            </div>
          </div>

          {/* 客户名称 */}
          <div>
            <label className="block text-[#57606a] text-sm font-medium mb-2">
              客户名称 <span className="text-[#8b949e] font-normal">(选填, 便于识别授权码归属)</span>
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              maxLength={100}
              placeholder="例如: 张三 / 微信ID / 订单号"
              className="w-full bg-[#f6f8fa] rounded-md py-3 px-4 text-[#1f2328] border border-[#d0d7de] focus:outline-none brand-ring"
            />
          </div>

          {/* 激活选项 */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={activateNow}
                onChange={(e) => setActivateNow(e.target.checked)}
                className="w-5 h-5 rounded accent-blue-500"
              />
              <span className="text-[#57606a] text-sm font-medium">
                生成后直接激活 <span className="text-[#8b949e] font-normal">(不勾选则为待授权状态, 需在授权列表手动激活)</span>
              </span>
            </label>
          </div>

          {/* 到期时间 */}
          <div>
            <label className="block text-[#57606a] text-sm font-medium mb-2">
              到期时间 <span className="text-[#8b949e] font-normal">(选填, 不选=永久有效)</span>
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="date"
                value={expireDate}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setExpireDate(e.target.value)}
                className="bg-[#f6f8fa] rounded-md py-3 px-4 text-[#1f2328] border border-[#d0d7de] focus:outline-none brand-ring"
              />
              <div className="flex flex-wrap gap-2">
                {[
                  { label: '7天', days: 7 },
                  { label: '30天', days: 30 },
                  { label: '90天', days: 90 },
                  { label: '1年', days: 365 }
                ].map(opt => (
                  <button
                    key={opt.days}
                    type="button"
                    onClick={() => {
                      const d = new Date();
                      d.setDate(d.getDate() + opt.days);
                      setExpireDate(d.toISOString().slice(0, 10));
                    }}
                    className="text-xs px-2.5 py-1.5 rounded-md border border-[#d0d7de] bg-white text-[#57606a] hover:bg-[#f6f8fa]"
                  >
                    {opt.label}
                  </button>
                ))}
                {expireDate && (
                  <button
                    type="button"
                    onClick={() => setExpireDate('')}
                    className="text-xs px-2.5 py-1.5 rounded-md border border-[#d0d7de] bg-white text-[#57606a] hover:bg-[#f6f8fa]"
                  >
                    永久
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 备注 */}
          <div>
            <label className="block text-[#57606a] text-sm font-medium mb-2">
              备注 <span className="text-[#8b949e] font-normal">(选填, 联系方式/购买渠道等)</span>
            </label>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="批量生成时, 客户名称和备注将应用到本批次所有授权码"
              className="w-full bg-[#f6f8fa] rounded-md py-3 px-4 text-[#1f2328] border border-[#d0d7de] focus:outline-none brand-ring resize-y"
            />
          </div>
        </div>
        
        <div className="flex flex-col gap-2 text-sm text-[#656d76]">
          <div className="flex items-center">
            <FiCheckSquare className="mr-2" />
            <span>默认生成后直接激活并可按需设置到期时间, 无需再去授权列表编辑</span>
          </div>
          <div className="flex items-center">
            <FiList className="mr-2" />
            <span>批量生成的授权码将显示在下方列表中</span>
          </div>
        </div>
      </motion.div>
      
      {/* 生成结果 */}
      {generateMutation.isLoading ? (
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-md shadow-sm p-6 flex items-center justify-center"
          style={{ minHeight: '300px' }}
        >
          <Loader size={50} />
        </motion.div>
      ) : generatedLicenses.length > 0 ? (
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-md shadow-sm p-6"
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-[#1f2328] flex items-center">
              <FiKey className="mr-2" /> 生成的授权码
            </h2>
            
            <div className="flex gap-3">
              <Button
                onClick={toggleSelectAll}
                variant="secondary"
                icon={<FiCheckSquare />}
                size="sm"
              >
                {selectedLicenses.length === generatedLicenses.length ? '取消全选' : '全选'}
              </Button>
              <Button
                onClick={copySelectedLicenses}
                variant="primary"
                icon={<FiCopy />}
                size="sm"
                disabled={selectedLicenses.length === 0}
              >
                复制选中 ({selectedLicenses.length})
              </Button>
            </div>
          </div>
          
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {generatedLicenses.map((license) => (
              <motion.div
                key={license.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
                className="flex items-center p-3 bg-[#f6f8fa] rounded-md"
              >
                <input
                  type="checkbox"
                  checked={selectedLicenses.includes(license.licenseKey)}
                  onChange={() => toggleSelectLicense(license.licenseKey)}
                  className="mr-3 w-5 h-5 rounded accent-blue-500"
                />
                <div className="flex-1 font-mono text-[#1f2328] overflow-x-auto">
                  {license.licenseKey}
                </div>
                {license.customerName && (
                  <span className="text-xs text-[#656d76] shrink-0 px-2 py-0.5 rounded-full bg-[#eaeef2]">
                    {license.customerName}
                  </span>
                )}
                <Button
                  onClick={() => copyToClipboard(license.licenseKey)}
                  variant="ghost"
                  icon={<FiCopy />}
                  className="p-1.5 text-[#656d76] hover:text-[#1f2328]"
                />
              </motion.div>
            ))}
          </div>
          
          <div className="flex flex-wrap justify-between items-center gap-3 mt-6 pt-4 border-t border-[#d0d7de]">
            <span className="text-[#656d76]">
              共生成 {generatedLicenses.length} 个授权码
            </span>
            <Button
              onClick={() => navigate('/licenses')}
              variant="secondary"
            >
              返回授权码列表
            </Button>
          </div>
        </motion.div>
      ) : null}
    </motion.div>
  );
};

export default CreateLicense;